// --------------------------------------------------------------
// Created On: 2025-06-23
// Author: Dimitra Weinstein
//
// Last Modified: 2025-11-06
// Modified By: Zachary Thomas
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment } from "react";
import { useNavigate } from "react-router-dom";
import SortedTableHeader from "../../../components/SortedTableHeader/SortedTableHeader";
import formatDashTimeString from "../../../utilities/formatDashTimeString";
import formatDateLocal from "../../../utilities/time/formatDateLocal";
import nowToUtcIso from "../../../utilities/time/nowToUtcIso";
import styles from "./QuoteListTable.module.scss";

// Table containing information on all sales quotes that fit within the passed-in filters.
export default function QuoteListTable(props: Props): Component {
  const navigate = useNavigate();
  // Formats the quote date by removing the time from the string then converting it into a slash format.
  function formatSalesQuoteDate(date: string | null): string {
    if (date !== null && date.trim() !== "") {
      let dateString = formatDateLocal(date);
      const newDate = new Date(dateString);
      dateString = newDate.toISOString().split("T")[0];
      return formatDashTimeString(dateString);
    } else {
      return "N/A";
    }
  }

  // Determines if the quote is active by checking if it has a sales order ID and if it was created within the last 30 days.
  function quoteIsActive(quote: SalesQuote): boolean {
    const thirtyDaysAgo = nowToUtcIso(30);
    return quote.salesOrderId === null && quote.createdUtc >= thirtyDaysAgo;
  }

  return (
    <Fragment>
      {!props.loading && props.salesQuotes.length === 0 && (
        <div className={`${styles.info} py-5 px-4`}>
          <h5 className="mb-4">
            <span>No sales quotes exist</span>
          </h5>
        </div>
      )}
      {!props.loading && props.salesQuotes.length > 0 && (
        <table className={`${styles.orderTable} table table-hover`}>
          <thead className={styles.header}>
            <tr>
              <SortedTableHeader
                name="Quote Number"
                index={1}
                selectedIndex={props.sortColumnIndex}
                selectedAscending={props.sortAscending}
                onClick={(index) => props.onUpdateSort(index)}
                className="col"
              />
              <SortedTableHeader
                name="Name"
                index={2}
                selectedIndex={props.sortColumnIndex}
                selectedAscending={props.sortAscending}
                onClick={(index) => props.onUpdateSort(index)}
                className="col"
              />
              <SortedTableHeader
                name="Quote Created"
                index={3}
                selectedIndex={props.sortColumnIndex}
                selectedAscending={props.sortAscending}
                onClick={(index) => props.onUpdateSort(index)}
                className="col"
              />
              <SortedTableHeader
                name="Sales Order Created"
                index={4}
                selectedIndex={props.sortColumnIndex}
                selectedAscending={props.sortAscending}
                onClick={(index) => props.onUpdateSort(index)}
                className="col"
              />
              <th className={`${styles.unsortedColumn} col`}>View Details/Submit Order</th>
            </tr>
          </thead>
          <tbody>
            {props.salesQuotes.map((quote) => (
              <tr
                data-test="sales-quotes-table-row"
                key={quote.salesQuoteId}
                className={quoteIsActive(quote) ? "" : "table-secondary"}
              >
                <td data-test="sales-quote-row-quote-number" className="align-middle">
                  {quote.salesQuoteId}
                </td>
                <td data-test="sales-quote-row-name" className="align-middle">
                  {quote.name}
                </td>
                <td data-test="sales-quote-row-quote-created" className="align-middle">
                  {formatSalesQuoteDate(quote.createdUtc)}
                </td>
                <td data-test="sales-quote-row-sales-order-created" className="align-middle">
                  {quote.receivedUtc !== null ? formatSalesQuoteDate(quote.receivedUtc) : "N/A"}
                </td>
                <td className="align-middle">
                  {quoteIsActive(quote) ? (
                    <button
                      data-test="sales-quote-submit-order-button"
                      className="btn btn-primary btn-sm text-center px-3"
                      onClick={() => navigate(`/submit-order/quote/${quote.salesQuoteId}`)}
                    >
                      <i className={`fa fa-fw fa-shopping-cart mx-2 ${styles.viewDetailsButton}`} />
                      Submit Order
                    </button>
                  ) : (
                    <button
                      data-test="sales-quote-view-details-button"
                      className="btn btn-primary btn-sm text-center px-4"
                      onClick={() => props.onUpdateSelection(quote.salesQuoteId)}
                    >
                      <i className={`fa fa-fw fa-external-link mx-1 ${styles.viewDetailsButton}`} />
                      View Details
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Fragment>
  );
}

interface Props {
  loading: boolean;
  salesQuotes: SalesQuote[];
  sortColumnIndex: number;
  sortAscending: boolean;
  onUpdateSort: (newIndex: number) => void;
  onUpdateSelection: (salesQuoteId: number) => void;
}

interface SalesQuote {
  salesQuoteId: number;
  name: string;
  salesOrderId: number | null;
  createdUtc: string;
  receivedUtc: string | null;
}
